import { useState } from "react";
import { Leaf, Waves, Home as HomeIcon, MapPinned, Footprints, ImageIcon, X, Bird } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "../components/ui/carousel";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { useI18n } from "../lib/i18n";

const BEACH_IMAGES = [
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/zcipjcxz_smbeach4.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/nlv7mpaq_beach5.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/5km8ahbz_beach6.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/48b8bt91_beach7.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/9o2zxqme_beach10.jpeg",
];

const MORASS_IMAGES = [
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/7fblenzg_morass.jpeg",
  "https://customer-assets.emergentagent.com/job_negril-beachfront/artifacts/roquopt0_morass1.jpeg",
];

const MAP_EMBED =
  "https://www.google.com/maps?q=R%26V+Villa+Negril+Jamaica&hl=it&ll=18.286856,-78.3390667&z=16&output=embed";

const CAROUSEL_OPTS = { loop: true };

export const About = () => {
  const { t, lang } = useI18n();
  const a = t.about;
  const [beachOpen, setBeachOpen] = useState(false);
  const [morassOpen, setMorassOpen] = useState(false);

  return (
    <section id="about" data-testid="about-section" className="relative bg-[#FAFAFA] py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="max-w-3xl">
          <span className="inline-block text-[#FFB800] font-bold tracking-[0.25em] text-xs md:text-sm uppercase">{a.kicker}</span>
          <h2 data-testid="about-title" className="mt-4 text-[#1A1A1A] font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>{a.title}</h2>
          <p className="mt-6 text-[#4A4A4A] text-base sm:text-lg leading-relaxed">{a.intro}</p>
        </div>

        <div className="mt-16 md:mt-24 grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 text-[#2D5A27]"><HomeIcon className="w-5 h-5" /><span className="font-semibold tracking-wide uppercase text-sm">{a.who_title}</span></div>
            <h3 className="mt-3 text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>{a.who_title}</h3>
          </div>
          <div className="lg:col-span-7">
            <p className="text-[#4A4A4A] text-base md:text-lg leading-relaxed whitespace-pre-line">{a.who_body}</p>
          </div>
        </div>

        <div className="mt-20 md:mt-28 grid lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <div className="flex items-center gap-3 text-[#2D5A27]"><Leaf className="w-5 h-5" /><span className="font-semibold tracking-wide uppercase text-sm">{a.property_title}</span></div>
            <h3 className="mt-3 text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>{a.property_title}</h3>
            <p className="mt-5 text-[#4A4A4A] text-base md:text-lg leading-relaxed">{a.property_body}</p>

            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white border border-black/5 p-5 md:p-6 shadow-sm">
                <div className="flex items-center gap-2 text-[#0D3B66] font-semibold"><Waves className="w-4 h-4" />{a.beach_title}</div>
                <p className="mt-2 text-sm md:text-base text-[#4A4A4A] leading-relaxed">{a.beach_body}</p>
              </div>
              <button
                type="button"
                onClick={() => setMorassOpen(true)}
                data-testid="nature-card-trigger"
                className="group text-left rounded-2xl bg-white border border-black/5 p-5 md:p-6 shadow-sm hover:shadow-md hover:border-[#2D5A27]/30 transition-all duration-300 cursor-pointer focus:outline-none focus:ring-4 focus:ring-[#2D5A27]/20"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[#2D5A27] font-semibold"><Leaf className="w-4 h-4" />{a.nature_title}</div>
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wider uppercase text-[#2D5A27]/70"><Bird className="w-3 h-3" />{MORASS_IMAGES.length}</span>
                </div>
                <p className="mt-2 text-sm md:text-base text-[#4A4A4A] leading-relaxed">{a.nature_body}</p>
                <span className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#2D5A27] group-hover:text-[#FFB800] transition-colors">
                  <ImageIcon className="w-3.5 h-3.5" />{a.nature_cta}
                </span>
              </button>
            </div>
          </div>

          <div className="lg:col-span-5 order-1 lg:order-2">
            <button
              type="button"
              onClick={() => setBeachOpen(true)}
              data-testid="beach-photo-trigger"
              aria-label={lang === "it" ? "Apri galleria foto Seven Mile Beach" : "Open Seven Mile Beach photo gallery"}
              className="group relative rounded-3xl overflow-hidden aspect-[4/5] shadow-xl w-full block cursor-pointer focus:outline-none focus:ring-4 focus:ring-[#FFB800]/40"
            >
              <img src={BEACH_IMAGES[0]} alt="Seven Mile Beach, Negril" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent" />
              <div className="absolute top-5 left-5 right-5 flex items-center justify-between text-white">
                <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase bg-white/15 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
                  <MapPinned className="w-3 h-3 text-[#FFB800]" />Seven Mile Beach
                </div>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full">
                  <ImageIcon className="w-3.5 h-3.5" />{BEACH_IMAGES.length}
                </span>
              </div>
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <span className="block text-xs uppercase tracking-[0.2em] opacity-80">{lang === "it" ? "100 m dalla villa" : "100m from the villa"}</span>
                <span className="block mt-1 text-lg font-bold">{lang === "it" ? "Tocca per vedere la spiaggia" : "Tap to explore the beach"}</span>
              </div>
            </button>
          </div>
        </div>

        <div className="mt-20 md:mt-28 rounded-3xl bg-[#F5F1E7] p-8 md:p-12 lg:p-16">
          <div className="grid lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 text-[#0D3B66]"><MapPinned className="w-5 h-5" /><span className="font-semibold tracking-wide uppercase text-sm">{a.location_title}</span></div>
              <h3 className="mt-3 text-2xl md:text-3xl font-bold text-[#1A1A1A]" style={{ fontFamily: "'Outfit', system-ui, sans-serif" }}>{a.location_title}</h3>
              <p className="mt-5 text-[#4A4A4A] text-base md:text-lg leading-relaxed">{a.location_body}</p>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl bg-white border border-black/5 p-6 md:p-7">
                <div className="flex items-center gap-2 text-[#2D5A27] font-semibold"><Footprints className="w-4 h-4" />10–15 min</div>
                <p className="mt-2 text-sm md:text-base text-[#4A4A4A] leading-relaxed">{a.location_city}</p>
              </div>
              <div className="rounded-2xl bg-white border border-black/5 p-6 md:p-7">
                <div className="flex items-center gap-2 text-[#0D3B66] font-semibold"><Waves className="w-4 h-4" />&lt; 100 m</div>
                <p className="mt-2 text-sm md:text-base text-[#4A4A4A] leading-relaxed">{a.location_sea}</p>
              </div>
            </div>
          </div>

          {/* Mappa interattiva - dimensione ridotta */}
          <div data-testid="location-map" className="mt-8 max-w-3xl mx-auto rounded-2xl overflow-hidden border border-black/5 shadow-md bg-white">
            <div className="relative w-full" style={{ paddingBottom: "48%" }}>
              <iframe title="R&V Villa - Google Maps" src={MAP_EMBED} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" className="absolute inset-0 w-full h-full border-0" />
            </div>
            <div className="flex items-center justify-between px-4 py-2.5 bg-white">
              <div className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                <MapPinned className="w-4 h-4 text-[#FFB800]" />
                <span className="font-semibold">R&V Villa</span>
                <span className="text-[#4A4A4A] hidden sm:inline text-xs">· Norman Manley Blv, Negril</span>
              </div>
              <a href="https://maps.app.goo.gl/MmZdrC3XA42m4HD49" target="_blank" rel="noopener noreferrer" data-testid="open-in-maps" className="text-xs font-semibold text-[#0D3B66] hover:text-[#FFB800] transition-colors">{a.open_in_maps}</a>
            </div>
          </div>
        </div>
      </div>

      <PhotoLightbox open={beachOpen} onClose={() => setBeachOpen(false)} lang={lang} images={BEACH_IMAGES} title="Seven Mile Beach · Negril" icon={Waves} testId="beach-lightbox" closeTestId="beach-lightbox-close" />
      <PhotoLightbox open={morassOpen} onClose={() => setMorassOpen(false)} lang={lang} images={MORASS_IMAGES} title={`${a.nature_title} · Morass`} icon={Leaf} testId="morass-lightbox" closeTestId="morass-lightbox-close" />
    </section>
  );
};

const PhotoLightbox = ({ open, onClose, lang, images, title, icon: Icon, testId, closeTestId }) => (
  <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
    <DialogContent data-testid={testId} className="max-w-5xl p-0 overflow-hidden bg-[#0D3B66] border-none [&>button]:hidden">
      <DialogTitle className="sr-only">{title}</DialogTitle>
      <div className="relative">
        <div className="absolute top-4 left-4 right-4 z-20 flex items-center justify-between">
          <span className="text-white/95 font-semibold text-sm md:text-base bg-black/40 backdrop-blur-md px-3 py-1.5 rounded-full inline-flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#FFB800]" />{title}
          </span>
          <button type="button" onClick={onClose} data-testid={closeTestId} aria-label={lang === "it" ? "Chiudi" : "Close"} className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md text-white hover:bg-[#FFB800] hover:text-[#1A1A1A] grid place-items-center transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
        <Carousel className="w-full" opts={CAROUSEL_OPTS}>
          <CarouselContent>
            {images.map((src, idx) => (
              <CarouselItem key={idx}>
                <div className="aspect-[4/3] md:aspect-[16/10] w-full bg-black flex items-center justify-center">
                  <img src={src} alt={`${title} ${idx + 1}`} className="w-full h-full object-contain" />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          {images.length > 1 && (
            <>
              <CarouselPrevious className="left-3 bg-white/15 backdrop-blur-md border-white/30 text-white hover:bg-[#FFB800] hover:text-[#1A1A1A]" />
              <CarouselNext className="right-3 bg-white/15 backdrop-blur-md border-white/30 text-white hover:bg-[#FFB800] hover:text-[#1A1A1A]" />
            </>
          )}
        </Carousel>
      </div>
    </DialogContent>
  </Dialog>
);

export default About;
